import React, { useMemo, useState, useEffect, useContext } from "react";
import { Box, Tabs, Tab, IconButton, TablePagination, Checkbox, FormControlLabel } from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import Header from "../../components/Header";
import IconsBar from "../../components/IconsBar/IconsBar";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import { createCollaborator, listCollaborators } from "../../api/endpoints/collaborators";
import JSZip from "jszip";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import {
  ActionsRow,
  Card,
  Container,
  ContentArea,
  ErrorText,
  FieldGroup,
  FormGrid,
  Input,
  Label,
  List,
  PageSubtitle,
  PageTitle,
  PrimaryButton,
  ResultGrid,
  ResultItem,
  ResultLabel,
  ResultValue,
  SecondaryButton,
  Select,
  StatusText,
  TitleArea,
} from "./styles";

const MONTHS = [
  { value: "janeiro", label: "Janeiro" },
  { value: "fevereiro", label: "Fevereiro" },
  { value: "março", label: "Março" },
  { value: "abril", label: "Abril" },
  { value: "maio", label: "Maio" },
  { value: "junho", label: "Junho" },
  { value: "julho", label: "Julho" },
  { value: "agosto", label: "Agosto" },
  { value: "setembro", label: "Setembro" },
  { value: "outubro", label: "Outubro" },
  { value: "novembro", label: "Novembro" },
  { value: "dezembro", label: "Dezembro" },
];


const normalizeEmployeeName = (fullName) => {
  return fullName
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, "_");
};

const buildLinesFromItems = (items) => {
  const lines = [];
  const tolerance = 2;

  items.forEach((item) => {
    const x = item.transform[4];
    const y = Math.round(item.transform[5]);
    let line = lines.find((candidate) => Math.abs(candidate.y - y) <= tolerance);
    if (!line) {
      line = { y, items: [] };
      lines.push(line);
    }
    line.items.push({ x, text: item.str });
  });

  return lines
    .sort((a, b) => b.y - a.y)
    .map((line) => {
      const text = line.items
        .sort((a, b) => a.x - b.x)
        .map((item) => item.text)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      return text;
    })
    .filter(Boolean);
};

const extractNameFromLines = (lines) => {
  // Palavras de empresa que devem ser ignoradas
  const companyWords = new Set([
    "colegio", "colégio", "eireli", "ltda", "me", "epp", "sa", "s/a",
    "educacao", "educação", "infantil", "ensino", "fundamental", "medio", "médio",
    "queiros", "andrade", "de", "edu", "inf"
  ]);
  
  // Palavras comuns que devem ser ignoradas (cabeçalhos de tabela, etc.)
  const ignoreWords = new Set([
    "descrição", "referência", "vencimentos", "descontos", "código",
    "cod", "cód", "nome", "cpf", "matrícula", "admissão", "cbo",
    "empresa", "competência", "período", "holerite", "contracheque",
    "folha", "pagamento", "salário", "total", "líquido", "bruto",
    "rua", "avenida", "av", "cnpj", "departamento", "data"
  ]);
  
  // Juntar todas as linhas em um texto único (como o Python faz)
  const fullText = lines.join(" ");
  
  // Encontrar posição de "Cód." (como no Python: texto_pagina.find("Cód."))
  const codIndex = fullText.search(/C[oó]d\.?/i);
  
  if (codIndex > 0) {
    // Pegar texto ANTES de "Cód." (como no Python: texto_pagina[0:posicao_final-2])
    let textBeforeCod = fullText.substring(0, codIndex - 2).trim();
    
    // Estratégia 1: Procurar por padrão "000129 NOME DO COLABORADOR" seguido de "Data Admissão"
    // O nome do colaborador geralmente vem depois de um código numérico e antes de "Data Admissão"
    const codeNamePattern = /(\d{4,})\s+([A-ZÁÉÍÓÚÂÊÔÇ][A-ZÁÉÍÓÚÂÊÔÇa-záéíóúâêôçãõ\s]{5,}?)(?:\s+Data\s+Admissão|CTPS|CBO|TECNICO|ADMINISTRADOR|PROFESSOR|AUXILIAR)/i;
    const codeNameMatch = textBeforeCod.match(codeNamePattern);
    if (codeNameMatch) {
      let candidate = codeNameMatch[2].trim();
      // Remover código se ainda estiver no início
      candidate = candidate.replace(/^\d+\s+/, "").trim();
      // Pegar apenas as primeiras 2-4 palavras (nome completo)
      const words = candidate.split(/\s+/).filter(w => w.trim().length > 0);
      if (words.length >= 2) {
        candidate = words.slice(0, Math.min(4, words.length)).join(" ");
        // Verificar se não contém palavras de empresa
        const firstWordLower = words[0].toLowerCase();
        if (!companyWords.has(firstWordLower) && candidate.length >= 5) {
          return candidate;
        }
      }
    }
    
    // Estratégia 2: Procurar por linha que contenha "Data Admissão" e pegar a linha anterior
    for (let i = 0; i < lines.length; i++) {
      if (/Data\s+Admissão|CTPS|CBO/i.test(lines[i])) {
        // Pegar linha anterior que deve conter o nome
        if (i > 0) {
          const prevLine = lines[i - 1].trim();
          // Procurar por padrão "000129 NOME" ou apenas "NOME"
          const nameMatch = prevLine.match(/(?:^\d{4,}\s+)?([A-ZÁÉÍÓÚÂÊÔÇ][A-ZÁÉÍÓÚÂÊÔÇa-záéíóúâêôçãõ]+(?:\s+[A-ZÁÉÍÓÚÂÊÔÇ][A-ZÁÉÍÓÚÂÊÔÇa-záéíóúâêôçãõ]+){1,3})/);
          if (nameMatch) {
            let candidate = nameMatch[1].trim();
            // Remover código se ainda estiver no início
            candidate = candidate.replace(/^\d+\s+/, "").trim();
            const words = candidate.split(/\s+/);
            // Verificar se não contém palavras de empresa
            if (words.length >= 2 && words.length <= 4) {
              const firstWord = words[0].toLowerCase();
              if (!companyWords.has(firstWord) && !ignoreWords.has(firstWord)) {
                return candidate;
              }
            }
          }
        }
      }
    }
    
    // Estratégia 3: Procurar por grupos de palavras que parecem nomes (ignorando empresa)
    // Remover caracteres especiais do início
    textBeforeCod = textBeforeCod.replace(/^[\d\W_]+/, "").trim();
    
    // Dividir em palavras
    const allWords = textBeforeCod.split(/\s+/).filter(w => w.trim().length > 0);
    
    for (let i = 0; i <= allWords.length - 2; i++) {
      // Tentar grupos de 2, 3 e 4 palavras
      for (let groupSize = 4; groupSize >= 2; groupSize--) {
        if (i + groupSize > allWords.length) continue;
        
        const group = allWords.slice(i, i + groupSize);
        const groupText = group.join(" ");
        const firstWord = group[0].trim();
        const lastWord = group[group.length - 1].trim();
        const firstWordLower = firstWord.toLowerCase();
        
        // Verificar se o grupo parece um nome (e não é empresa)
        const isValidName = 
          // Primeira palavra deve começar com maiúscula
          /^[A-ZÁÉÍÓÚÂÊÔÇ]/.test(firstWord) &&
          // Não deve ser palavra de empresa
          !companyWords.has(firstWordLower) &&
          // Não deve conter palavras ignoradas
          !group.some(w => ignoreWords.has(w.toLowerCase())) &&
          // Não deve ser principalmente números ou caracteres especiais
          /[A-ZÁÉÍÓÚÂÊÔÇa-záéíóúâêôçãõ]{3,}/.test(groupText) &&
          // Não deve terminar com dois pontos, vírgula, etc
          !/[:\-]$/.test(lastWord) &&
          // Deve ter pelo menos 5 caracteres no total
          groupText.length >= 5;
        
        if (isValidName) {
          // Limpar o nome (remover caracteres especiais do início/fim)
          let candidate = groupText
            .replace(/^[\d\W_]+/, "")
            .replace(/[\W_]+$/, "")
            .trim();
          
          if (candidate && candidate.length >= 5) {
            return candidate;
          }
        }
      }
    }
  }
  
  return null;
};

const Holerites = () => {
  const { user } = useContext(AuthContext);
  const { showSuccess } = useNotification();
  const [tabIndex, setTabIndex] = useState(0);
  const [pdfFile, setPdfFile] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState(MONTHS[new Date().getMonth()].value);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [summary, setSummary] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [collaboratorName, setCollaboratorName] = useState("");
  const [collaboratorResult, setCollaboratorResult] = useState(null);
  const [collaboratorError, setCollaboratorError] = useState("");
  const [collaboratorLoading, setCollaboratorLoading] = useState(false);
  const [accessSearch, setAccessSearch] = useState("");
  const [accessList, setAccessList] = useState([]);
  const [accessTotal, setAccessTotal] = useState(0);
  const [accessPage, setAccessPage] = useState(1);
  const [accessRowsPerPage, setAccessRowsPerPage] = useState(10);
  const [accessOnlyResponsibles, setAccessOnlyResponsibles] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState("");

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const loadAccessList = async () => {
    setAccessLoading(true);
    setAccessError("");
    try {
      const res = await listCollaborators({
        name: accessSearch.trim() || undefined,
        onlyResponsibles: accessOnlyResponsibles,
        page: accessPage,
        limit: accessRowsPerPage,
      });
      setAccessList(res.collaborators || []);
      setAccessTotal(res.total ?? 0);
    } catch (err) {
      setAccessError(err?.response?.data?.message || "Erro ao carregar colaboradores.");
      setAccessList([]);
      setAccessTotal(0);
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    if (tabIndex !== 2) return;
    const t = setTimeout(() => loadAccessList(), 300);
    return () => clearTimeout(t);
  }, [tabIndex, accessSearch, accessOnlyResponsibles, accessPage, accessRowsPerPage]);

  useEffect(() => {
    if (tabIndex === 2) setAccessPage(1);
  }, [accessSearch, accessOnlyResponsibles]);

  const handleCopyAccessData = async (c) => {
    const text = `Nome: ${c.Nome || ""}\nUsuário: ${c.login || ""}\nSenha: ${c.password || ""}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      showSuccess("Dados copiados para a área de transferência.");
    } catch (err) {
      setAccessError("Não foi possível copiar.");
    }
  };

  // Obter unidade do usuário logado e normalizar
  const unit = useMemo(() => {
    if (!user?.unit) return "patos";
    return user.unit === "CAMPINA GRANDE" ? "campina grande" : "patos";
  }, [user]);

  // Configurar worker do PDF.js - usar worker local da pasta public
  useEffect(() => {
    if (typeof window !== "undefined") {
      pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
    }
  }, []);

  const competenceFolder = useMemo(() => month, [month]);

  const resetState = () => {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }
    setSummary(null);
    setDownloadUrl("");
    setProgress({ current: 0, total: 0 });
    setError("");
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setPdfFile(file || null);
    resetState();
  };

  const processPdf = async () => {
    if (!pdfFile) {
      setError("Selecione o PDF com os contracheques.");
      return;
    }
    if (!year || !/^\d{4}$/.test(year)) {
      setError("Informe o ano no formato YYYY.");
      return;
    }

    setProcessing(true);
    setError("");
    setSummary(null);
    setDownloadUrl("");

    try {
      const buffer = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(buffer);
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

      const totalPages = pdf.numPages;
      setProgress({ current: 0, total: totalPages });

      const docsByName = {};
      const missingPages = [];

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
        const pageNumber = pageIndex + 1;
        const page = await pdf.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const lines = buildLinesFromItems(textContent.items || []);
        const name = extractNameFromLines(lines);

        if (!name) {
          missingPages.push(pageNumber);
          setProgress({ current: pageNumber, total: totalPages });
          continue;
        }

        const normalizedName = normalizeEmployeeName(name);
        
        // Debug: log apenas as primeiras 5 páginas
        if (pageNumber <= 5) {
          console.log(`Página ${pageNumber}: Nome encontrado="${name}", Normalizado="${normalizedName}"`);
        }
        
        if (!docsByName[normalizedName]) {
          docsByName[normalizedName] = {
            fullName: name,
            doc: await PDFDocument.create(),
            pages: 0,
          };
        }

        const targetDoc = docsByName[normalizedName].doc;
        const [copiedPage] = await targetDoc.copyPages(pdfDoc, [pageIndex]);
        targetDoc.addPage(copiedPage);
        docsByName[normalizedName].pages += 1;

        setProgress({ current: pageNumber, total: totalPages });
      }
      
      // Debug: mostrar resumo dos colaboradores encontrados
      console.log("Colaboradores identificados:", Object.keys(docsByName).map(k => ({
        normalizado: k,
        nomeCompleto: docsByName[k].fullName,
        paginas: docsByName[k].pages
      })));

      const zip = new JSZip();
      const basePath = `${unit}/${year}/${competenceFolder}`;

      const fileEntries = Object.entries(docsByName);
      for (const [normalizedName, entry] of fileEntries) {
        const pdfBytes = await entry.doc.save();
        zip.file(`${basePath}/${normalizedName}.pdf`, pdfBytes);
      }

      for (const pageNumber of missingPages) {
        const doc = await PDFDocument.create();
        const [copiedPage] = await doc.copyPages(pdfDoc, [pageNumber - 1]);
        doc.addPage(copiedPage);
        const pdfBytes = await doc.save();
        zip.file(`${basePath}/nao_identificados/pagina_${pageNumber}.pdf`, pdfBytes);
      }

      // Verificar se há arquivos para adicionar ao ZIP
      if (fileEntries.length === 0 && missingPages.length === 0) {
        throw new Error("Nenhum colaborador foi identificado e não há páginas para processar.");
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      
      // Limpar URL anterior se existir
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
      
      // Criar nova URL para o ZIP
      const url = URL.createObjectURL(zipBlob);
      setDownloadUrl(url);
      
      setSummary({
        totalPages,
        collaborators: fileEntries.length,
        missingPages,
      });
    } catch (err) {
      console.error("Erro ao processar PDF:", err);
      setError(
        err.message || "Não foi possível processar o PDF. Verifique o arquivo e tente novamente."
      );
      setDownloadUrl("");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!downloadUrl || !summary) {
      setError("Processe o PDF primeiro antes de baixar o ZIP.");
      return;
    }
    
    try {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `holerites_${unit}_${year}_${competenceFolder}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Erro ao baixar ZIP:", err);
      setError("Erro ao baixar o arquivo ZIP. Tente novamente.");
    }
  };

  return (
    <Container>
      <Header />
      <IconsBar />
      <ContentArea>
        <TitleArea>
          <PageTitle>RH - Contracheques</PageTitle>
          <PageSubtitle>
            Utilize o separador de contracheques ou cadastre colaboradores manualmente.
          </PageSubtitle>
        </TitleArea>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="RH Contracheques"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Separador de Contracheques" />
            <Tab label="Cadastro de Colaboradores" />
            <Tab label="Consulta de Acesso ao App" />
          </Tabs>
        </Box>

        {tabIndex === 0 && (
          <>
            <Card>
              <FormGrid>
                <FieldGroup>
                  <Label>Ano</Label>
                  <Input
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    placeholder="YYYY"
                    maxLength={4}
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label>Competência</Label>
                  <Select value={month} onChange={(e) => setMonth(e.target.value)}>
                    {MONTHS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FieldGroup>
                <FieldGroup>
                  <Label>PDF com múltiplos colaboradores</Label>
                  <Input type="file" accept="application/pdf" onChange={handleFileChange} />
                </FieldGroup>
              </FormGrid>

              <ActionsRow>
                <PrimaryButton disabled={processing} onClick={processPdf}>
                  {processing ? "Processando..." : "Processar PDF"}
                </PrimaryButton>
                <SecondaryButton disabled={!downloadUrl} onClick={handleDownload}>
                  Baixar ZIP
                </SecondaryButton>
                {processing && (
                  <StatusText>
                    Processando página {progress.current} de {progress.total}
                  </StatusText>
                )}
              </ActionsRow>

              {error && <ErrorText>{error}</ErrorText>}
            </Card>

            {summary && (
              <Card>
                <ResultGrid>
                  <ResultItem>
                    <ResultLabel>Páginas analisadas</ResultLabel>
                    <ResultValue>{summary.totalPages}</ResultValue>
                  </ResultItem>
                  <ResultItem>
                    <ResultLabel>Colaboradores identificados</ResultLabel>
                    <ResultValue>{summary.collaborators}</ResultValue>
                  </ResultItem>
                  <ResultItem>
                    <ResultLabel>Páginas sem nome</ResultLabel>
                    <ResultValue>{summary.missingPages.length}</ResultValue>
                  </ResultItem>
                </ResultGrid>

                {summary.missingPages.length > 0 && (
                  <>
                    <PageSubtitle>
                      Páginas sem identificação foram salvas em{" "}
                      <strong>nao_identificados</strong>.
                    </PageSubtitle>
                    <List>
                      {summary.missingPages.map((page) => (
                        <li key={page}>Página {page}</li>
                      ))}
                    </List>
                  </>
                )}
              </Card>
            )}
          </>
        )}

        {tabIndex === 1 && (
          <>
            <Card>
              <FormGrid>
                <FieldGroup>
                  <Label>Nome completo do colaborador</Label>
                  <Input
                    value={collaboratorName}
                    onChange={(e) => setCollaboratorName(e.target.value)}
                    placeholder="Digite o nome completo"
                  />
                </FieldGroup>
              </FormGrid>
              <ActionsRow>
                <PrimaryButton
                  disabled={collaboratorLoading || !collaboratorName.trim()}
                  onClick={async () => {
                    try {
                      setCollaboratorError("");
                      setCollaboratorResult(null);
                      setCollaboratorLoading(true);
                      const response = await createCollaborator({
                        name: collaboratorName.trim(),
                      });
                      setCollaboratorResult(response.data);
                    } catch (err) {
                      const message =
                        err?.response?.data?.message ||
                        "Erro ao cadastrar colaborador. Tente novamente.";
                      setCollaboratorError(message);
                    } finally {
                      setCollaboratorLoading(false);
                    }
                  }}
                >
                  {collaboratorLoading ? "Cadastrando..." : "Cadastrar colaborador"}
                </PrimaryButton>
              </ActionsRow>
              {collaboratorError && <ErrorText>{collaboratorError}</ErrorText>}
            </Card>

            {collaboratorResult && (
              <Card>
                <PageSubtitle>Colaborador cadastrado com sucesso:</PageSubtitle>
                <ResultGrid>
                  <ResultItem>
                    <ResultLabel>Nome</ResultLabel>
                    <ResultValue>{collaboratorResult.name}</ResultValue>
                  </ResultItem>
                  <ResultItem>
                    <ResultLabel>Usuário (login)</ResultLabel>
                    <ResultValue>{collaboratorResult.login}</ResultValue>
                  </ResultItem>
                  <ResultItem>
                    <ResultLabel>Senha</ResultLabel>
                    <ResultValue>{collaboratorResult.password}</ResultValue>
                  </ResultItem>
                </ResultGrid>
                <PageSubtitle>
                  Entregue essas credenciais ao colaborador. Ele poderá alterar a senha depois,
                  se necessário.
                </PageSubtitle>
              </Card>
            )}
          </>
        )}

        {tabIndex === 2 && (
          <Card>
            <PageSubtitle style={{ marginBottom: 16 }}>
              Colaboradores com acesso ao app (isColaborator = true). Pesquise por nome (underscores
              são ignorados).
            </PageSubtitle>
            <FormGrid style={{ marginBottom: 16 }}>
              <FieldGroup>
                <Label>Pesquisar por nome</Label>
                <Input
                  value={accessSearch}
                  onChange={(e) => setAccessSearch(e.target.value)}
                  placeholder="Digite o nome para filtrar (ex.: ayala renata)"
                />
              </FieldGroup>
              <FieldGroup style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={accessOnlyResponsibles}
                      onChange={(e) => setAccessOnlyResponsibles(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label="Apenas responsáveis"
                  style={{ marginLeft: 0 }}
                />
              </FieldGroup>
            </FormGrid>
            {accessError && <ErrorText>{accessError}</ErrorText>}
            {accessLoading && <StatusText>Carregando...</StatusText>}
            {!accessLoading && !accessError && (
              <Box component="div" sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #E3F2FD", textAlign: "left" }}>
                      <th style={{ padding: "10px 12px", color: "#666", fontWeight: 600 }}>Nome</th>
                      <th style={{ padding: "10px 12px", color: "#666", fontWeight: 600 }}>Usuário</th>
                      <th style={{ padding: "10px 12px", color: "#666", fontWeight: 600 }}>Senha</th>
                      <th style={{ padding: "10px 12px", color: "#666", fontWeight: 600 }}>Responsável</th>
                      <th style={{ padding: "10px 12px", color: "#666", fontWeight: 600, width: 56 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {accessList.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: 24, color: "#666", textAlign: "center" }}>
                          Nenhum colaborador encontrado.
                        </td>
                      </tr>
                    ) : (
                      accessList.map((c) => (
                        <tr key={c.id} style={{ borderBottom: "1px solid #E3F2FD" }}>
                          <td style={{ padding: "10px 12px" }}>{c.Nome || "—"}</td>
                          <td style={{ padding: "10px 12px", color: "#333" }}>{c.login || "—"}</td>
                          <td style={{ padding: "10px 12px", color: "#333" }}>{c.password ?? "—"}</td>
                          <td style={{ padding: "10px 12px" }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "4px 10px",
                                borderRadius: 8,
                                fontSize: 12,
                                fontWeight: 600,
                                backgroundColor: c.isResponsable ? "#E8F5E9" : "#FFF3E0",
                                color: c.isResponsable ? "#2E7D32" : "#E65100",
                              }}
                            >
                              {c.isResponsable ? "Sim" : "Não"}
                            </span>
                          </td>
                          <td style={{ padding: "8px" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyAccessData(c)}
                              title="Copiar nome, usuário e senha"
                              aria-label="Copiar dados de acesso"
                              sx={{ color: "#0b4f6c" }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
            )}
            {!accessLoading && !accessError && accessTotal > 0 && (
              <TablePagination
                component="div"
                count={accessTotal}
                page={accessPage - 1}
                onPageChange={(e, newPage) => setAccessPage(newPage + 1)}
                rowsPerPage={accessRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setAccessRowsPerPage(parseInt(e.target.value, 10));
                  setAccessPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{ borderTop: "1px solid #E3F2FD" }}
              />
            )}
          </Card>
        )}
      </ContentArea>
    </Container>
  );
};

export default Holerites;
